import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';

// ============ DIRECT MESSAGING SERVICE ============
// 1:1 conversations live in the top-level `conversations` collection; each
// conversation owns a `messages` subcollection. See firestore.rules for access.

const MAX_LAST_MESSAGE_LENGTH = 200;

// Trim + truncate the text used for the conversation preview (lastMessage).
const truncate = (text) => {
  const trimmed = (text || '').trim();
  return trimmed.length > MAX_LAST_MESSAGE_LENGTH
    ? `${trimmed.slice(0, MAX_LAST_MESSAGE_LENGTH)}…`
    : trimmed;
};

/**
 * Find an existing 1:1 conversation between two users, or create one.
 * Deterministic lookup: query conversations where participants array-contains
 * the current uid, then filter client-side for one that also contains otherUid
 * (avoids creating duplicate conversations for the same pair).
 * @returns {Promise<string>} the conversation id.
 */
export const getOrCreateConversation = async (currentUid, currentName, otherUid, otherName) => {
  const conversationsRef = collection(db, 'conversations');

  // Look for an existing conversation containing both participants.
  const existingQuery = query(conversationsRef, where('participants', 'array-contains', currentUid));
  const snapshot = await getDocs(existingQuery);
  const existing = snapshot.docs.find((d) => {
    const participants = d.data().participants || [];
    return participants.includes(otherUid);
  });
  if (existing) {
    return existing.id;
  }

  // None found — create a new conversation.
  const newConversation = await addDoc(conversationsRef, {
    participants: [currentUid, otherUid],
    participantNames: {
      [currentUid]: currentName || 'Member',
      [otherUid]: otherName || 'Member'
    },
    lastMessage: '',
    lastMessageAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    // Per-participant unread message counts. Cleared via markConversationRead.
    unread: {
      [currentUid]: 0,
      [otherUid]: 0
    }
  });
  return newConversation.id;
};

/**
 * Subscribe to all conversations the user participates in.
 * Sorted by lastMessageAt desc CLIENT-SIDE to avoid needing a composite index.
 * @returns {() => void} the unsubscribe function.
 */
export const subscribeToConversations = (uid, callback) => {
  const conversationsRef = collection(db, 'conversations');
  const conversationsQuery = query(conversationsRef, where('participants', 'array-contains', uid));

  return onSnapshot(conversationsQuery, (snapshot) => {
    const conversations = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    conversations.sort((a, b) => {
      const aTime = a.lastMessageAt?.toMillis ? a.lastMessageAt.toMillis() : 0;
      const bTime = b.lastMessageAt?.toMillis ? b.lastMessageAt.toMillis() : 0;
      return bTime - aTime;
    });
    callback(conversations);
  });
};

/**
 * Subscribe to messages within a conversation, ordered by createdAt asc.
 * @returns {() => void} the unsubscribe function.
 */
export const subscribeToMessages = (conversationId, callback) => {
  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'));

  return onSnapshot(messagesQuery, (snapshot) => {
    const messages = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    callback(messages);
  });
};

/**
 * Send a message: add it to the messages subcollection AND update the parent
 * conversation's preview fields (lastMessage / lastMessageAt / updatedAt).
 */
export const sendMessage = async (conversationId, senderId, senderName, text) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return;

  const messagesRef = collection(db, 'conversations', conversationId, 'messages');
  await addDoc(messagesRef, {
    senderId,
    senderName: senderName || 'Member',
    text: trimmed,
    createdAt: serverTimestamp()
  });

  // Read the conversation to find the recipient (the participant who isn't the sender).
  const conversationRef = doc(db, 'conversations', conversationId);
  const conversationSnap = await getDoc(conversationRef);
  const participants = conversationSnap.exists()
    ? conversationSnap.data().participants || []
    : [];
  const recipientUid = participants.find((uid) => uid !== senderId);

  // Update the conversation preview AND bump the recipient's unread counter.
  const conversationUpdate = {
    lastMessage: truncate(trimmed),
    lastMessageAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  if (recipientUid) {
    conversationUpdate[`unread.${recipientUid}`] = increment(1);
  }
  await updateDoc(conversationRef, conversationUpdate);

  // Notify the recipient so the bell/notification list surfaces the new message.
  if (recipientUid) {
    await addDoc(collection(db, 'notifications'), {
      userId: recipientUid,
      type: 'message.received',
      title: `New message from ${senderName || 'Member'}`,
      message: trimmed.slice(0, 120),
      data: { conversationId },
      read: false,
      createdAt: serverTimestamp()
    });
  }
};

/**
 * Clear the unread counter for a user on a conversation (called when they open it).
 */
export const markConversationRead = async (conversationId, uid) => {
  if (!conversationId || !uid) return;
  const conversationRef = doc(db, 'conversations', conversationId);
  await updateDoc(conversationRef, { [`unread.${uid}`]: 0 });
};

/**
 * Fetch a single conversation document (for the thread header / participant name).
 * @returns {Promise<object|null>} { id, ...data } or null if missing.
 */
export const getConversation = async (conversationId) => {
  const conversationRef = doc(db, 'conversations', conversationId);
  const snapshot = await getDoc(conversationRef);
  return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
};
